import { z } from 'zod';
import * as FlatModels from "../../internal";

export const TextExportDisplayType = z.enum([
  'TextStructured',
  'TextWithoutInfo',
  'TextInJson'
]);
export type TextExportDisplayType = z.infer<typeof TextExportDisplayType>;

export const TextExportDisplayTypeArray = [TextExportDisplayType.enum.TextStructured, TextExportDisplayType.enum.TextWithoutInfo, TextExportDisplayType.enum.TextInJson].map(value => { return { value: value, name: 'enum.text_export.' + value }; });

export const TextExportSettingsSchema = z.object({
  displayType: TextExportDisplayType,
  htmlTags: z.boolean(),
});

export const TextExportSchema = z.object({
  id: z.string().uuid(),
});

export type TextExport = z.infer<typeof TextExportSchema>;

const TextExportAnnotationKeymapLabelObjectSchema = z.object({
  id: z.string().uuid(),
  text: z.string().nullable(),
});

export type TextExportAnnotationKeymapLabelObject = z.infer<typeof TextExportAnnotationKeymapLabelObjectSchema>;

const TextExportAnnotationObjectSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  description: z.string().nullable(),
  keymapLabels: z.array(TextExportAnnotationKeymapLabelObjectSchema).optional(),
});

export type TextExportAnnotationObject = z.infer<typeof TextExportAnnotationObjectSchema>;


const TextExportPoiObjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string().optional(),
  note: z.string().optional(),
});

export type TextExportPoiObject = z.infer<typeof TextExportPoiObjectSchema>;

const TextExportGroupObjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export type TextExportGroupObject = z.infer<typeof TextExportGroupObjectSchema>;

const TextExportQuestionChoiceObjectSchema = z.object({
  id: z.string().uuid(),
  label: z.string().optional(),
  solution: z.union([z.boolean(), z.number().int()]).optional(),
});

export type TextExportQuestionChoiceObject = z.infer<typeof TextExportQuestionChoiceObjectSchema>;

const TextExportQuestionObjectSchema = z.object({
  id: z.string().uuid(),
  content: z.string().optional(),
  solutionText: z.string().nullable().optional(),
  explanation: z.string().nullable(),
  type: z.nativeEnum(FlatModels.QuestionEntityModels.QuestionType.enum).nullable().optional(),
  choices: z.array(TextExportQuestionChoiceObjectSchema),
});

export type TextExportQuestionObject = z.infer<typeof TextExportQuestionObjectSchema>;

const TextExportMediaCopyrightObjectSchema = z.object({
  id: z.string().uuid(),
  copyright: z.string(),
  extra: z.string().nullable(),
});

export type TextExportMediaCopyrightObject = z.infer<typeof TextExportMediaCopyrightObjectSchema>;

const TextExportMediaObjectSchema = z.object({
  id: z.string().uuid(),
  type: z.nativeEnum(FlatModels.MediaEntityModels.MediaType.enum).nullable().optional(),
  url: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  posterUrl: z.string().nullable(),
  name: z.string().nullable(),
  copyrights: z.array(TextExportMediaCopyrightObjectSchema).optional(),
});

export type TextExportMediaObject = z.infer<typeof TextExportMediaObjectSchema>;

const TextExportVocabularyGroupWordObjectSchema = z.object({
  id: z.string().uuid(),
  key: z.string().optional(),
  value: z.string().optional(),
});

const TextExportVocabularyGroupObjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string().optional(),
  words: z.array(TextExportVocabularyGroupWordObjectSchema),
});

export type TextExportVocabularyGroupObject = z.infer<typeof TextExportVocabularyGroupObjectSchema>;

const BaseTextExportObjectSchema = z.object({
  id: z.string().uuid(),
  editedAt: z.string().optional(),
  nodeId: z.string().uuid(),
  route: z.string(),
  type: z.string(),
});

const SlideTextExportObjectSchema = BaseTextExportObjectSchema.extend({
  body: z.string().nullable(),
  header: z.string().nullable(),
  title: z.string().nullable(),
  slideId: z.number().optional(),
  pdfPath: z.string().optional(),
  authorPdfPath: z.string().optional(),
  media: z.array(TextExportMediaObjectSchema),
  annotations: z.array(TextExportAnnotationObjectSchema),
  pois: z.array(TextExportPoiObjectSchema),
  groups: z.array(TextExportGroupObjectSchema),
  questions: z.array(TextExportQuestionObjectSchema),
  description: z.string().nullable(),
  comments: z.string().nullable(),
});

export type SlideTextExportObject = z.infer<typeof SlideTextExportObjectSchema>;

const VocabularyTextExportObjectSchema = BaseTextExportObjectSchema.extend({
  vocabularyGroups: z.array(TextExportVocabularyGroupObjectSchema),
  body: z.string().nullable(),
  comments: z.string().nullable(),
});

export type VocabularyTextExportObject = z.infer<typeof VocabularyTextExportObjectSchema>;

const FolderTextExportObjectSchema = BaseTextExportObjectSchema.extend({
  title: z.string().nullable(),
  body: z.string().nullable(),
  description: z.string().nullable(),
  comments: z.string().nullable(),
});

export type FolderTextExportObject = z.infer<typeof FolderTextExportObjectSchema>;

const GridTextExportObjectSchema = BaseTextExportObjectSchema.extend({
  title: z.string().nullable(),
  pdfPath: z.string().optional(),
  authorPdfPath: z.string().optional(),
  body: z.string().nullable(),
  description: z.string().nullable(),
  comments: z.string().nullable(),
});

export type GridTextExportObject = z.infer<typeof GridTextExportObjectSchema>;

const TextExportObjectSchema = z.union([
  SlideTextExportObjectSchema,
  VocabularyTextExportObjectSchema,
  GridTextExportObjectSchema,
  FolderTextExportObjectSchema,
]);

export type TextExportObject = z.infer<typeof TextExportObjectSchema>;

const TextExportArgTypeSchema = z.object({
  textExports: z.array(TextExportObjectSchema),
  type: TextExportDisplayType,
  keepHTMLTags: z.boolean(),
});

export type TextExportArgType = z.infer<typeof TextExportArgTypeSchema>;

const TextExportResultSchema = FlatModels.EntityControlModels.EntityQueryResultSchema.extend({
  entity: z.object({
    id: z.string().uuid(),
    results: z.array(TextExportObjectSchema),
  })
});

export type TextExportResult = z.infer<typeof TextExportResultSchema>;